import { AlertCircle, Check, Minus, Search, X } from 'lucide-react';
import { Checkbox as C, RadioGroup as R, Switch as S, ToggleGroup as TG } from 'radix-ui';
import {
  createContext,
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
  useContext,
  useId,
} from 'react';
import { cn } from '../lib/cn';

/* -------------------------------------------------------------------- Field */

interface FieldContextValue {
  id: string;
  describedBy?: string;
  invalid: boolean;
  required: boolean;
}

const FieldContext = createContext<FieldContextValue | null>(null);

export function useFieldContext(): FieldContextValue | null {
  return useContext(FieldContext);
}

export interface FieldProps {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  /** Show "(optional)" next to the label instead of marking required fields. */
  optional?: boolean;
  labelHidden?: boolean;
  className?: string;
  /** Stable id for the control, when other code needs to focus it. Set it here, never on the control itself (that would orphan the label). */
  id?: string;
  children: ReactNode;
}

/** Visible label text for a composite control that supplies its own accessible group name. */
export function FieldLabel({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cn('mb-1.5 block text-sm font-medium text-fg', className)}>{children}</span>;
}

/**
 * Wraps one control with its label, hint and error, and wires the ids and ARIA
 * attributes automatically: Input / Textarea / Select / Combobox / DatePicker
 * pick them up from context, so forms cannot forget a label.
 */
export function Field({ label, hint, error, required, optional, labelHidden, className, id: idProp, children }: FieldProps) {
  const autoId = useId();
  const id = idProp ?? autoId;
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined;
  return (
    <FieldContext.Provider value={{ id, describedBy, invalid: !!error, required: !!required }}>
      <div className={cn('min-w-0', className)}>
        <label htmlFor={id} className={cn('mb-1.5 flex items-baseline gap-1 text-sm font-medium text-fg', labelHidden && 'sr-only')}>
          {label}
          {required ? (
            <>
              <span aria-hidden className="text-danger-fg">
                *
              </span>
              <span className="sr-only">(required)</span>
            </>
          ) : optional ? (
            <span className="text-xs font-normal text-subtle">(optional)</span>
          ) : null}
        </label>
        {children}
        {hint ? (
          <p id={hintId} className="mt-1.5 text-sm text-muted">
            {hint}
          </p>
        ) : null}
        {error ? (
          <p id={errorId} role="alert" className="mt-1.5 flex items-start gap-1.5 text-sm text-danger-fg">
            <AlertCircle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
            {error}
          </p>
        ) : null}
      </div>
    </FieldContext.Provider>
  );
}

export const controlBox = cn(
  'flex w-full items-center rounded-md border border-input bg-surface text-base text-fg shadow-xs',
  'transition-[border-color,box-shadow] duration-(--dur-fast)',
  'focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/25',
  'has-[input:disabled]:cursor-not-allowed has-[input:disabled]:bg-sunken has-[input:disabled]:text-disabled',
  'has-[textarea:disabled]:cursor-not-allowed has-[textarea:disabled]:bg-sunken has-[textarea:disabled]:text-disabled',
);

export const controlInvalid = 'border-danger-solid focus-within:border-danger-solid focus-within:ring-danger-solid/25';

/** ARIA + id props shared by every form control, taken from the surrounding <Field>. */
export function useControlProps(props: { id?: string; invalid?: boolean; 'aria-describedby'?: string }) {
  const field = useFieldContext();
  return {
    id: props.id ?? field?.id,
    'aria-describedby': props['aria-describedby'] ?? field?.describedBy,
    'aria-invalid': (props.invalid ?? field?.invalid) || undefined,
    'aria-required': field?.required || undefined,
    invalid: props.invalid ?? field?.invalid ?? false,
  };
}

/* -------------------------------------------------------------------- Input */

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
  leading?: ReactNode;
  trailing?: ReactNode;
  invalid?: boolean;
  wrapperClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { leading, trailing, invalid: invalidProp, wrapperClassName, className, id, 'aria-describedby': describedBy, ...props },
  ref,
) {
  const control = useControlProps({ id, invalid: invalidProp, 'aria-describedby': describedBy });
  return (
    <div className={cn(controlBox, 'h-(--control-h)', control.invalid && controlInvalid, wrapperClassName)}>
      {leading ? <span className="pl-3 text-subtle [&_svg]:size-4">{leading}</span> : null}
      <input
        ref={ref}
        id={control.id}
        aria-describedby={control['aria-describedby']}
        aria-invalid={control['aria-invalid']}
        aria-required={control['aria-required']}
        className={cn(
          'h-full min-w-0 flex-1 bg-transparent px-3 text-base outline-none placeholder:text-subtle disabled:cursor-not-allowed',
          leading ? 'pl-2' : undefined,
          className,
        )}
        {...props}
      />
      {trailing ? <span className="flex items-center pr-3 text-sm text-subtle">{trailing}</span> : null}
    </div>
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
  wrapperClassName?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { invalid: invalidProp, wrapperClassName, className, id, rows = 3, 'aria-describedby': describedBy, ...props },
  ref,
) {
  const control = useControlProps({ id, invalid: invalidProp, 'aria-describedby': describedBy });
  return (
    <div className={cn(controlBox, control.invalid && controlInvalid, wrapperClassName)}>
      <textarea
        ref={ref}
        id={control.id}
        rows={rows}
        aria-describedby={control['aria-describedby']}
        aria-invalid={control['aria-invalid']}
        aria-required={control['aria-required']}
        className={cn(
          'min-h-20 w-full resize-y bg-transparent px-3 py-2 text-base outline-none placeholder:text-subtle disabled:cursor-not-allowed',
          className,
        )}
        {...props}
      />
    </div>
  );
});

/* -------------------------------------------------------------- SearchInput */

export interface SearchInputProps extends Omit<InputProps, 'value' | 'onChange' | 'leading' | 'trailing' | 'type'> {
  value: string;
  onValueChange: (value: string) => void;
  clearLabel?: string;
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  { value, onValueChange, clearLabel = 'Clear search', placeholder = 'Search…', ...props },
  ref,
) {
  return (
    <Input
      ref={ref}
      type="search"
      role="searchbox"
      value={value}
      onChange={(e) => onValueChange(e.target.value)}
      placeholder={placeholder}
      leading={<Search aria-hidden />}
      trailing={
        value ? (
          <button
            type="button"
            aria-label={clearLabel}
            onClick={() => onValueChange('')}
            className="-mr-1.5 grid size-6 place-items-center rounded text-subtle hover:bg-sunken hover:text-fg"
          >
            <X className="size-3.5" aria-hidden />
          </button>
        ) : null
      }
      className="[&::-webkit-search-cancel-button]:hidden"
      {...props}
    />
  );
});

/* ----------------------------------------------------------------- Checkbox */

export interface CheckboxProps extends Omit<React.ComponentProps<typeof C.Root>, 'children'> {
  label: ReactNode;
  description?: ReactNode;
}

export function Checkbox({ label, description, className, id, ...props }: CheckboxProps) {
  const autoId = useId();
  const cid = id ?? autoId;
  return (
    <div className={cn('flex items-start gap-2.5', className)}>
      <C.Root
        id={cid}
        className={cn(
          'mt-0.5 grid size-[1.125rem] shrink-0 place-items-center rounded-[5px] border border-input bg-surface text-on-brand shadow-xs outline-offset-2',
          'transition-colors data-[state=checked]:border-brand data-[state=checked]:bg-brand data-[state=indeterminate]:border-brand data-[state=indeterminate]:bg-brand',
          'disabled:cursor-not-allowed disabled:opacity-50',
        )}
        {...props}
      >
        <C.Indicator>{props.checked === 'indeterminate' ? <Minus className="size-3.5" aria-hidden /> : <Check className="size-3.5" aria-hidden />}</C.Indicator>
      </C.Root>
      <label htmlFor={cid} className="min-w-0 cursor-pointer text-base leading-snug text-fg">
        {label}
        {description ? <span className="mt-0.5 block text-sm text-muted">{description}</span> : null}
      </label>
    </div>
  );
}

/* ------------------------------------------------------------------- Switch */

export interface SwitchProps extends Omit<React.ComponentProps<typeof S.Root>, 'children'> {
  label: ReactNode;
  description?: ReactNode;
}

export function Switch({ label, description, className, id, ...props }: SwitchProps) {
  const autoId = useId();
  const sid = id ?? autoId;
  return (
    <div className={cn('flex items-start justify-between gap-4', className)}>
      <label htmlFor={sid} className="min-w-0 cursor-pointer text-base text-fg">
        {label}
        {description ? <span className="mt-0.5 block text-sm text-muted">{description}</span> : null}
      </label>
      <S.Root
        id={sid}
        className="relative mt-0.5 h-5 w-9 shrink-0 rounded-full bg-border-strong transition-colors data-[state=checked]:bg-brand disabled:cursor-not-allowed disabled:opacity-50"
        {...props}
      >
        <S.Thumb className="block size-4 translate-x-0.5 rounded-full bg-white shadow-sm transition-transform duration-(--dur-fast) data-[state=checked]:translate-x-[1.125rem]" />
      </S.Root>
    </div>
  );
}

/* --------------------------------------------------------------- RadioGroup */

export interface RadioOption {
  value: string;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
}

export function RadioGroup({
  options,
  value,
  defaultValue,
  onValueChange,
  className,
  orientation = 'vertical',
  ...props
}: {
  options: RadioOption[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  className?: string;
  orientation?: 'vertical' | 'horizontal';
  'aria-label'?: string;
  'aria-labelledby'?: string;
}) {
  const uid = useId();
  return (
    <R.Root
      value={value}
      defaultValue={defaultValue}
      onValueChange={onValueChange}
      orientation={orientation}
      className={cn(orientation === 'horizontal' ? 'flex flex-wrap gap-x-6 gap-y-2' : 'grid gap-2.5', className)}
      {...props}
    >
      {options.map((o) => (
        <div key={o.value} className="flex items-start gap-2.5">
          <R.Item
            id={`${uid}-${o.value}`}
            value={o.value}
            disabled={o.disabled}
            className="mt-0.5 grid size-[1.125rem] shrink-0 place-items-center rounded-full border border-input bg-surface shadow-xs data-[state=checked]:border-brand disabled:opacity-50"
          >
            <R.Indicator className="size-2.5 rounded-full bg-brand" />
          </R.Item>
          <label htmlFor={`${uid}-${o.value}`} className="cursor-pointer text-base leading-snug text-fg">
            {o.label}
            {o.description ? <span className="mt-0.5 block text-sm text-muted">{o.description}</span> : null}
          </label>
        </div>
      ))}
    </R.Root>
  );
}

/* --------------------------------------------------------- SegmentedControl */

export interface SegmentedOption<T extends string> {
  value: T;
  label: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onValueChange,
  size = 'md',
  className,
  ...aria
}: {
  options: SegmentedOption<T>[];
  value: T;
  onValueChange: (value: T) => void;
  size?: 'sm' | 'md';
  className?: string;
  'aria-label'?: string;
}) {
  return (
    <TG.Root
      type="single"
      value={value}
      onValueChange={(v) => {
        if (v) onValueChange(v as T);
      }}
      className={cn('inline-flex rounded-lg bg-sunken p-0.5', className)}
      {...aria}
    >
      {options.map((o) => (
        <TG.Item
          key={o.value}
          value={o.value}
          disabled={o.disabled}
          className={cn(
            'inline-flex items-center justify-center gap-1.5 rounded-md px-3 font-medium text-muted transition-colors',
            'hover:text-fg data-[state=on]:bg-surface data-[state=on]:text-fg data-[state=on]:shadow-xs disabled:opacity-50',
            '[&_svg]:size-4',
            size === 'sm' ? 'h-7 text-sm' : 'h-[calc(var(--control-h)-0.25rem)] text-base',
          )}
        >
          {o.icon}
          {o.label}
        </TG.Item>
      ))}
    </TG.Root>
  );
}

/* --------------------------------------------------------------- ScaleInput */

const scaleTone = (n: number, max: number) => {
  const ratio = n / max;
  if (ratio <= 0.3) return 'data-[state=on]:border-success-solid data-[state=on]:bg-success-bg data-[state=on]:text-success-fg';
  if (ratio <= 0.6) return 'data-[state=on]:border-warning-solid data-[state=on]:bg-warning-bg data-[state=on]:text-warning-fg';
  return 'data-[state=on]:border-danger-solid data-[state=on]:bg-danger-bg data-[state=on]:text-danger-fg';
};

/** 0..max rating (pain score). Accessible as a radio group; arrow keys move between values. */
export function ScaleInput({
  value,
  onValueChange,
  min = 0,
  max = 10,
  lowLabel = 'No pain',
  highLabel = 'Worst pain',
  label,
  className,
}: {
  value: number | null;
  onValueChange: (value: number) => void;
  min?: number;
  max?: number;
  lowLabel?: string;
  highLabel?: string;
  label: string;
  className?: string;
}) {
  const values = Array.from({ length: max - min + 1 }, (_, i) => min + i);
  return (
    <div className={className}>
      <TG.Root
        type="single"
        aria-label={label}
        value={value === null ? '' : String(value)}
        onValueChange={(v) => {
          if (v !== '') onValueChange(Number(v));
        }}
        className="grid gap-1"
        style={{ gridTemplateColumns: `repeat(${values.length}, minmax(0, 1fr))` }}
      >
        {values.map((n) => (
          <TG.Item
            key={n}
            value={String(n)}
            aria-label={`${n} out of ${max}`}
            className={cn(
              'grid h-(--control-h) place-items-center rounded-md border border-border-strong bg-surface text-base font-medium tabular-nums text-muted transition-colors',
              'hover:bg-surface-2',
              scaleTone(n, max),
            )}
          >
            {n}
          </TG.Item>
        ))}
      </TG.Root>
      <div className="mt-1 flex justify-between text-xs text-subtle">
        <span>{lowLabel}</span>
        <span>{highLabel}</span>
      </div>
    </div>
  );
}
